import { PropsWithChildren, ReactNode } from 'react';
import { SpacingToken, Stack, StackProps, Typography } from '@optimus-web/core';

interface ContentSectionProps extends Omit<StackProps, 'vertical' | 'children'> {
    title: ReactNode;
    contentSpacing?: SpacingToken;
}

/**
 * To be used inside a ContentSection component to create a sub-section with a title.
 *
 * @param props.title - The title of the content section.
 * @param [props.titleSize='small'] - The size of the title text.
 * @param [props.spacing='100'] - The spacing between the title elements and the children elements.
 * @param [props.contentSpacing] - The spacing between the children elements. If not given, the same value as the spacing prop will be used.
 * @param props.children - The children elements to be rendered inside the content section.
 * @param [stackProps] - Additional properties to be passed to the outermost Stack component.
 */
export const SubContentSection = ({
    title,
    spacing = '100',
    contentSpacing,
    children,
    ...stackProps
}: PropsWithChildren<ContentSectionProps>) => (
    <Stack {...stackProps} spacing={spacing} vertical>
        <Typography textStyle="bodyMediumStrong" as="div">
            {title}
        </Typography>
        <Typography textStyle="bodyMedium" variant="secondary" as="div">
            <Stack spacing={contentSpacing ?? spacing} vertical>
                {children}
            </Stack>
        </Typography>
    </Stack>
);
